import type { ReactNode } from 'react';

import { DefinitionTooltip } from '#/components/content/DefinitionTooltip';
import { cn } from '#/utils/cn';

export interface RangeSegment {
  /** Optional label rendered below this segment. */
  label?: string;
  /**
   * Optional description shown when `value` falls within this segment; overrides
   * the component-level `description`. Pass a function to vary the text by the
   * exact value within this segment (e.g. a boundary value like beta `1`).
   */
  description?: ReactNode | ((value: number) => ReactNode);
  /** Lower bound (inclusive); `null` for an open-ended low end (e.g. "<250M"). */
  min: number | null;
  /** Upper bound (exclusive); `null` for an open-ended high end (e.g. ">200B"). */
  max: number | null;
  /**
   * When true, `max` is treated as inclusive so a value equal to `max` lands in
   * this segment (with the marker at its top) instead of the next one. Defaults
   * to exclusive.
   */
  maxInclusive?: boolean;
}

interface RangeSegmentsProps {
  /** The value to plot across the full range. */
  value: number;
  /** Heading describing what the value represents. Used as a fallback when the active segment has no `description`. */
  description?: ReactNode;
  /** Ordered, contiguous segments from low to high. */
  segments: RangeSegment[];
  /** External value formatting function; a nullish result falls back to ''. */
  format?: (value: number) => string | undefined;
  /** Optional glossary tooltip */
  tooltip?: { termSlug: string; label: ReactNode; definition?: ReactNode };
  className?: string;
}

/* Index of the segment whose [min, max) interval contains `value` (max is
   inclusive when the segment sets `maxInclusive`). */
function activeSegmentIndex(segments: RangeSegment[], value: number): number {
  return segments.findIndex(
    ({ min, max, maxInclusive }) =>
      (min == null || value >= min) &&
      (max == null || (maxInclusive ? value <= max : value < max)),
  );
}

/* Position by value unless it's open-ended, then we position in the middle */
function fractionWithinSegment(segment: RangeSegment, value: number): number {
  const { min, max } = segment;
  if (min == null || max == null || max <= min) {
    return 0.5;
  }
  const fraction = (value - min) / (max - min);
  return Math.min(1, Math.max(0, fraction));
}

export function RangeSegments({
  value,
  description,
  segments,
  format = String,
  tooltip,
  className,
}: RangeSegmentsProps) {
  if (!segments.length) {
    return null;
  }

  const activeIndex = activeSegmentIndex(segments, value);
  // Guard against a value that matches no segment (gapped/misordered bounds).
  const safeIndex = activeIndex === -1 ? 0 : activeIndex;
  const activeSegment = segments[safeIndex];

  if (!activeSegment) {
    return null;
  }

  const fraction = fractionWithinSegment(activeSegment, value);
  const percent = ((safeIndex + fraction) / segments.length) * 100;

  const formatted = format(value) ?? '';
  const activeLabel = activeSegment.label;
  // The active segment's description wins; a function resolves against the exact
  // value so one segment can vary its text. Fall back to the component-level prop.
  const segmentDescription =
    typeof activeSegment.description === 'function'
      ? activeSegment.description(value)
      : activeSegment.description;
  const activeDescription = segmentDescription ?? description;

  const rangeAriaLabel = activeLabel
    ? `${activeLabel} range`
    : `Range ${safeIndex + 1} of ${segments.length}`;

  return (
    <div className={cn('flow flow-8', className)}>
      <p className="text-static-14 text-right font-semibold">{formatted}</p>
      {activeDescription && (
        <p className="text-static-14 text-fg-header flow-0 text-right">
          {activeDescription}
        </p>
      )}

      <div className="flex items-start gap-12">
        <div
          className="flow flow-8 flex-1"
          role="img"
          aria-label={rangeAriaLabel}
        >
          {/* segmented track + marker */}
          <div className="relative flex h-10 gap-2 rounded">
            {segments.map((_, i) => (
              <div
                key={i}
                className={cn(
                  'last:border-r-0; flex-1 rounded-full',
                  i === safeIndex ? 'bg-slate-90' : 'bg-steel-30',
                )}
              />
            ))}
            <div
              className="bg-gray-10 border-blue-90 absolute -top-2 size-14 -translate-x-1/2 rounded-full border-2"
              style={{ left: `${percent}%` }}
            />
          </div>

          {/* per-segment labels */}
          {segments.some((segment) => segment.label) && (
            <div className="text-static-12 text-fg flex gap-2">
              {segments.map((segment, i) => (
                <span key={i} className="flex-1 px-2">
                  {segment.label}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* tooltip */}
        {tooltip && (
          <DefinitionTooltip
            termSlug={tooltip.termSlug}
            definition={tooltip.definition}
            className="shrink-0"
          >
            {tooltip.label}
          </DefinitionTooltip>
        )}
      </div>
    </div>
  );
}
